import { isAxiosError } from 'axios';

/**
 * React Query's default `retry` (#272): one retry for a network error or a 5xx, never for a
 * 4xx except 408. A refused read (403), a missing row (404) or a rejected request (409, 422,
 * 429) cannot succeed on a resend, and under `retry: 1` every one was sent twice, so each
 * refused read reached the server's error log twice.
 *
 * Only an AxiosError carries a status. The response interceptor (`services/api.ts`) always
 * rejects an AxiosError, though not always the original one: the consent signal rejects the
 * original; a CSRF or post-refresh replay rejects the replay's own error; a failed refresh
 * rejects the refresh call's error, so its status is the refresh route's (normally 401, not
 * retried). A refresh that got NO answer (#524: offline, timeout, 5xx) keeps the session and
 * rejects that error, so it is retried once like any network error or 5xx, which tries the
 * refresh once more; that is intended. A status is therefore read whenever the server answered.
 * A malformed refresh body rejects a plain Error and is retried once too. The other exception is the
 * plain `Error('No refresh token')` thrown when a 401 finds no stored refresh token: it has no
 * status, so it is retried once, as before, harmlessly, while the page redirects to login.
 * `queryRetry.interceptor.test.ts` pins this through the real interceptor.
 *
 * `failureCount` is the number of failures so far, starting at 0, so `< 1` is `retry: 1`.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status !== undefined && status >= 400 && status < 500 && status !== 408) return false;
  return failureCount < 1;
}
